import {computed, inject, Injectable} from '@angular/core';
import {toSignal} from '@angular/core/rxjs-interop';
import {firstValueFrom, Observable, Subject} from 'rxjs';
import {map, shareReplay, skip, startWith, switchMap, take, tap} from 'rxjs/operators';
import {AccountService, UserWithSeat} from 'src/api';

@Injectable({
  providedIn: 'root',
})
export class LawFirmUsersCacheService {
  private readonly accountService = inject(AccountService);
  private readonly refreshTrigger = new Subject<void>();
  private emitCount = 0;

  private readonly users$ = this.refreshTrigger.pipe(
    startWith(void 0),
    switchMap(() => this.accountService.listLawFirmUsers()),
    map((users) => this.sortUsers(users)),
    tap(() => {
      this.emitCount += 1;
    }),
    shareReplay({bufferSize: 1, refCount: false}),
  );

  /** Firm users from the shared cache (empty until first load). */
  readonly users = toSignal(this.users$, {initialValue: [] as UserWithSeat[]});

  readonly usersById = computed(() => new Map(this.users().map((u) => [u.id, u])));

  getUsers(): Observable<UserWithSeat[]> {
    return this.users$;
  }

  getUser(id: string): UserWithSeat | undefined {
    return this.usersById().get(id);
  }

  refresh(): void {
    this.refreshTrigger.next();
  }

  /** Force a reload and resolve with the fresh list (avoids stale shareReplay). */
  reload(): Promise<UserWithSeat[]> {
    if (this.emitCount === 0) {
      return firstValueFrom(this.users$.pipe(take(1)));
    }
    const result = firstValueFrom(this.users$.pipe(skip(1), take(1)));
    this.refreshTrigger.next();
    return result;
  }

  private sortUsers(users: UserWithSeat[]): UserWithSeat[] {
    const regularUsers = users.filter((user) => !this.isUserPending(user));
    const pendingUsers = users.filter((user) => this.isUserPending(user));

    regularUsers.sort((a, b) => {
      const firstNameComparison = (a.first_name || '')
        .toLowerCase()
        .localeCompare((b.first_name || '').toLowerCase());
      if (firstNameComparison !== 0) {
        return firstNameComparison;
      }
      return (a.last_name || '').toLowerCase().localeCompare((b.last_name || '').toLowerCase());
    });

    pendingUsers.sort((a, b) =>
      (a.mail || '').toLowerCase().localeCompare((b.mail || '').toLowerCase()),
    );

    return [...regularUsers, ...pendingUsers];
  }

  private isUserPending(user: UserWithSeat): boolean {
    return !user.first_name && !user.last_name;
  }
}
